import { HTMLAttributes, useRef } from 'react'
import { useIsomorphicLayoutEffect } from 'react-use'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import Link from 'next/link'
import { useRouter } from 'next/router'

import AudioIcon from '@/components/base/AudioIcon'
import Container from '@/components/base/Container'
import ScrollIcon from '@/components/base/ScrollIcon'
import SVGIcon from '@/components/base/SVGIcon'
import SwipeIcon from '@/components/base/SwipeIcon'
import Typography from '@/components/base/Typography'
import { useMixer, useMixerStore } from '@/contexts/mixer'
import { useStore } from '@/contexts/store'
import { useTimeline } from '@/contexts/timeline'
import { useLoader } from '@/store/loader'
import { isLgScreen } from '@/utils/screen'

import { cn } from '@/lib/utils'

export type FooterProps = HTMLAttributes<HTMLElement>

const Footer = ({ className, ...props }: FooterProps) => {
  const { menu, strings } = useStore()
  const root = useRef<HTMLElement>(null!)
  const muted = useMixerStore((state) => state.muted)
  const { toggleSound } = useMixer()
  const { context, contextSafe } = useGSAP({ scope: root })
  const timeline = useTimeline()
  const router = useRouter()

  const reveal = contextSafe(() => {
    console.log('footer.reveal')

    const selector = context.selector || gsap.utils.selector(root.current)
    const copy = selector('[data-motion-copy]')[0]
    const legals = selector('[data-motion-legal]')
    const scroll = selector('[data-motion-scroll]')
    const socials = selector('[data-motion-social]')
    const audio = selector('[data-motion-audio]')
    const swipe = selector('[data-motion-swipe]')
    const panel = selector('[data-motion-panel]')

    const timeline = gsap.timeline()

    timeline
      .fromTo(
        copy,
        { y: 20, autoAlpha: 0 },
        { y: 0, autoAlpha: 1, duration: 0.8, ease: 'power3.out' },
        !isLgScreen() ? 0.6 : 0
      )
      .fromTo(
        legals,
        { y: 20, autoAlpha: 0 },
        { y: 0, autoAlpha: 1, duration: 0.8, ease: 'power3.out', stagger: 0.04 },
        !isLgScreen() ? 0.64 : 0.04
      )

    if (!isLgScreen()) {
      timeline
        .fromTo(panel, { yPercent: 100 }, { yPercent: 0, duration: 1.2, ease: 'power3.inOut' }, 0)
        .fromTo(swipe, { scaleY: 0 }, { scaleY: 1, duration: 1, ease: 'power3.inOut' }, 0.64)
    } else {
      timeline
        .fromTo(
          scroll,
          { y: 20, autoAlpha: 0 },
          { y: 0, autoAlpha: 1, duration: 1, ease: 'power3.out' },
          0.12
        )
        .fromTo(
          socials,
          { y: 20, autoAlpha: 0 },
          { y: 0, autoAlpha: 1, duration: 1, ease: 'power3.out', stagger: 0.04 },
          0.16
        )
        .fromTo(
          audio,
          { y: 20, autoAlpha: 0 },
          { y: 0, autoAlpha: 1, duration: 1, ease: 'power3.out' },
          0.32
        )
    }

    return timeline
  })

  useIsomorphicLayoutEffect(() => {
    const unsubscribe = useLoader.subscribe(
      (state) => state.compiled,
      (compiled) => {
        if (compiled) {
          unsubscribe()
          if (router.asPath === '/') {
            timeline.add(reveal(), !isLgScreen() ? 3 : 3.2)
          }
        }
      }
    )

    return () => {
      unsubscribe()
    }
  }, [])

  return (
    <footer ref={root} className={cn('static', className)} {...props}>
      <Container
        className={cn([
          'fixed bottom-0 left-0 right-0 z-footer lg:bottom-[4rem]',
          'flex items-end py-[2rem] lg:py-0'
        ])}
      >
        <span
          className="absolute left-0 top-0 h-full w-full bg-black-dark lg:hidden"
          data-motion-panel
        />
        <div className="relative flex grow flex-col flex-wrap space-y-spacing-xs lg:flex-row lg:space-x-spacing-xxs lg:space-y-0">
          <div className="flex" data-motion-copy>
            <Typography variant="small" className="opacity-50">
              {strings?.copyright.replace('[YEAR]', new Date().getFullYear())}
            </Typography>
          </div>
          <ul className="flex space-x-spacing-xxs">
            {menu?.legals_menu.menu.map(({ url, target, title }, index) => (
              <li key={index} data-motion-legal>
                <Link
                  href={url}
                  target={target}
                  className={cn([
                    'flex pointer:hover:opacity-50',
                    'transition-opacity duration-300 ease-out-cubic',
                    {
                      'pointer-events-none': url === '#'
                    }
                  ])}
                >
                  <Typography variant="small">{title}</Typography>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <SwipeIcon
          className="absolute bottom-[2rem] right-margin top-[2rem] pointer:hidden"
          data-motion-swipe
        />

        <div className="-mb-[0.2rem] hidden items-center lg:flex lg:space-x-spacing-xs xl:space-x-spacing-sm">
          <ScrollIcon data-motion-scroll>
            <Typography variant="regular" className="text-text-accent-dark">
              {strings?.scroll}
            </Typography>
          </ScrollIcon>

          <ul className="flex items-center space-x-spacing-xxs">
            {menu?.social_menu.menu.map(({ url, target, title, classes }, index) => (
              <li key={index} className="flex" data-motion-social>
                <a
                  href={url}
                  target={target}
                  className={cn([
                    '-m-spacing-xxs flex p-spacing-xxs',
                    'pointer:hover:opacity-50',
                    'transition-opacity duration-300 ease-out-cubic'
                  ])}
                >
                  <span className="sr-only">{title}</span>
                  <SVGIcon
                    symbol={`${classes}-icon`}
                    className="h-[1.6rem] w-[1.6rem] fill-text-primary-dark"
                  />
                </a>
              </li>
            ))}
          </ul>

          <AudioIcon
            muted={muted}
            className="h-[1.6rem] w-auto touch:hidden [&>svg]:-translate-y-[0.4rem]"
            data-motion-audio
            onClick={(event) => {
              event.stopPropagation()
              toggleSound()
            }}
          />
        </div>
      </Container>
    </footer>
  )
}

export default Footer
